import { $Types } from '@Ravnur-Inc/core/typings/tsx';
import { Prop } from '@Ravnur-Inc/decorators';
import { Vue, Options, setup } from 'vue-class-component';

import ChipsInput from '@/components/chips-input/chips-input';

import ModalService from '@/helpers/modal.service';
import LookupRepository from '@/repositories/lookup-repository';
import { StandardOption } from '@/types/Component';
import { LookupEntity, LookupEntity$Type } from '@/types/LookupEntity';
import {
  Media$Privacy,
  Media$Details,
  Media$ExternalPage,
  Media$Type,
  PRIVACY_LABELS,
  PLAYLIST_MEDIA_TYPES,
  PRIVACY_DESCS,
  PRIVACIES,
  PUBLIC_APP_PRIVACIES,
  PUBLOC_PRIVACY_DESCS,
} from '@/types/Media';
import { User$Compact } from '@/types/User';
import PlaylistMediaRepository, { PlaylistItem } from '@/repositories/playlist-media-repository';
import useSecurityStore from '@/store/security';

import './media-permission-editor.scss';

const CN = 'media-permission-editor';

export type MediaPermission = Pick<
  Media$Details,
  'type' | 'privacy' | 'allowedUsers' | 'allowExternalPage'
>;

type Props = {
  value: MediaPermission;
  owner: User$Compact;
  mediaId?: string;
  originPrivacy?: Media$Privacy;
};

type Emits = {
  onInput: (value: MediaPermission) => void;
};

@Options({
  emits: ['input', 'update:value'],
})
export default class MediaPermissionEditor extends Vue {
  declare $props: $Types<Props, Emits, 'value'>;
  @Prop({}) public readonly value: MediaPermission;
  @Prop({}) public readonly owner: User$Compact;
  @Prop({}) public readonly mediaId?: string;
  @Prop({}) public readonly originPrivacy: Media$Privacy | undefined;

  security = setup(() => useSecurityStore());

  playlistItems: PlaylistItem[] = [];

  get allowPages(): Array<{ id: Media$ExternalPage; label: string }> {
    return [
      {
        id: Media$ExternalPage.NONE,
        label: this.$t('media', 'allow-external-page__none'),
      },
      {
        id: Media$ExternalPage.PUBLIC,
        label: this.$t('media', 'allow-external-page__public'),
      },
    ];
  }

  get playlistRepository() {
    if (!this.mediaId) {
      return;
    }

    return new PlaylistMediaRepository(this.mediaId);
  }

  get mediaPrivacy() {
    return this.value.privacy;
  }

  get allowedUsers() {
    return this.value.allowedUsers;
  }

  get allowExternalPage() {
    return this.value.allowExternalPage === Media$ExternalPage.PUBLIC;
  }

  set allowExternalPage(value: boolean) {
    const allowExternalPage = value ? Media$ExternalPage.PUBLIC : Media$ExternalPage.NONE;
    this.handleExternalPageChanged(allowExternalPage);
  }

  get ownerAsLookupEntity(): LookupEntity {
    return {
      id: this.owner.id,
      name: this.owner.displayName,
    };
  }

  get privacies(): StandardOption[] {
    return this.security.isPublicApplication
      ? PUBLIC_APP_PRIVACIES.map(this.privacy2option)
      : PRIVACIES.map(this.privacy2option);
  }

  get usersRepository() {
    return new LookupRepository(LookupEntity$Type.USERS);
  }

  get isDocument() {
    return this.value.type === Media$Type.DOCUMENT;
  }

  get isPlaylist() {
    return PLAYLIST_MEDIA_TYPES.includes(this.value.type);
  }

  get hasRestrictedItem() {
    return this.playlistItems.some((item) => item.privacy === Media$Privacy.RESTRICTED);
  }

  get hasItemWithoutPublicPage() {
    return this.playlistItems.some((item) => item.allowExternalPage === Media$ExternalPage.NONE);
  }

  get playlistPublicDisabled() {
    return this.isPlaylist && (this.hasRestrictedItem || this.hasItemWithoutPublicPage);
  }

  render() {
    const isRestricted = this.mediaPrivacy === Media$Privacy.RESTRICTED;

    return (
      <div class={CN}>
        <chosen
          class={`${CN}__field`}
          options={this.privacies}
          value={this.mediaPrivacy}
          onInput={this.handlePrivacyChanged}
        />

        <div v-show={isRestricted}>
          <ChipsInput
            class={`${CN}__field`}
            items={this.allowedUsers}
            label={this.$t('media', 'field__allowed-users')}
            persistent={[this.ownerAsLookupEntity]}
            propName="name"
            repository={this.usersRepository}
            onChange={this.changeAllowedUsers}
          />
        </div>

        <checkbox
          class={`${CN}__field`}
          label={this.$t('media', 'field__allow-page')}
          v-show={!this.isDocument}
          vModel={[this.allowExternalPage, 'value']}
        />
      </div>
    );
  }

  async loadPlaylistItems() {
    if (!this.isPlaylist || !this.playlistRepository) {
      return;
    }

    try {
      this.playlistItems = await this.playlistRepository.load();
    } catch (e) {
      this.$processException(e);
    }
  }

  private privacy2option(privacy: Media$Privacy): StandardOption {
    const title = this.$t('media', PRIVACY_LABELS[privacy]);
    // Shitty code alert!
    const desc = this.security.isPublicApplication
      ? this.$t('media', PUBLOC_PRIVACY_DESCS[privacy])
      : this.$t('media', PRIVACY_DESCS[privacy]);

    return { id: privacy, label: `${title}: ${desc}` };
  }

  private async handlePrivacyChanged(privacy: Media$Privacy) {
    const { originPrivacy: old } = this;

    let tkey = '';
    if (privacy === Media$Privacy.RESTRICTED) {
      if (old === Media$Privacy.PUBLIC) {
        tkey = 'confirm__to-restricted-privacy';
      } else if (old === Media$Privacy.UNLISTED) {
        tkey = 'confirm__unlisted-to-restricted';
      }
    }

    if (tkey) {
      const msg = this.$t('media', tkey);
      const res = await ModalService.confirm(msg);
      if (!res) {
        return;
      }
    }

    const isRestricted = privacy === Media$Privacy.RESTRICTED;
    const allowExternalPage = isRestricted ? Media$ExternalPage.NONE : this.value.allowExternalPage;
    const allowedUsers = isRestricted ? [this.ownerAsLookupEntity] : [];

    this.change({ privacy, allowExternalPage, allowedUsers });
  }

  private async handleExternalPageChanged(allowExternalPage: Media$ExternalPage) {
    let { mediaPrivacy } = this;

    if (allowExternalPage === Media$ExternalPage.NONE) {
      this.change({ allowExternalPage, privacy: mediaPrivacy });

      return;
    }

    if (mediaPrivacy !== Media$Privacy.RESTRICTED && !this.playlistPublicDisabled) {
      this.change({ allowExternalPage, privacy: mediaPrivacy });

      return;
    }

    let msg = this.$t('media', 'confirm__to-ext-public-with-restricted-privacy');

    if (this.playlistPublicDisabled) {
      msg = this.hasRestrictedItem
        ? this.$t('media', 'properties-tab__external-page-confirm-playlist-restricted')
        : this.$t('media', 'properties-tab__external-page-confirm-playlist');
    }

    const modalOptions = {
      confirmText: this.$t('media', 'confirm__enable-public-page'),
      cancelText: this.playlistPublicDisabled
        ? this.$t('common', 'action__ok')
        : this.$t('common', 'action__cancel'),
      modalClasses: this.playlistPublicDisabled ? `${CN}__playlist-privacy-modal` : '',
    };

    const res = await ModalService.confirm(msg, modalOptions).catch(() => false);

    if (!res) return;

    mediaPrivacy = Media$Privacy.PUBLIC;

    this.change({ allowExternalPage, privacy: mediaPrivacy });
  }

  private changeAllowedUsers(allowedUsers: LookupEntity[]) {
    this.change({ allowedUsers });
  }

  private change(data: Partial<MediaPermission>) {
    const value = { ...this.value, ...data };
    this.$emit('input', value);
    this.$emit('update:value', value);
  }

  mounted() {
    this.loadPlaylistItems();
  }
}
